import { Model } from 'backbone';
import _ from 'lodash';
import currency from 'currency.js'

const _toCurrency = val => {
	if (!_.isNumber(val)) {
		return val;
	}

	const
		currency_str = currency(val, { precision: 0 }).format(),
		is_negative = currency_str[0] === '-';

	return is_negative ? `-$${currency_str.slice(1)}` : `$${currency_str}`;
};
const _toPercent = val => _.isNumber(val) ? `${val}%` : val;
const _toDefault = val => val;

export class StatsBox extends Model {
	get defaults() {
		return {
			round_id: 1,
			max_players_size: 2,
			SQUADS_PATH: process.env.SQUADS_PATH,
			compare: [],
			rounds: [],
			has_assistant_coach: false,
			is_coach_stats: false,
			colors: ['red', 'green', 'blue', 'yellow'],
			stats: [
				{
					is_coach_only: true,
					title: () => `Round ${this.get('round_id')} Projected`,
					key: 'stats.proj_score',
					processing: _toDefault,
				},
				{
					is_coach_only: true,
					title: 'Break Even',
					key: 'stats.break_even',
					processing: _toDefault,
				},
				{
					is_coach_only: true,
					title: 'Projected Price Change',
					key: 'stats.proj_price_change_next_round',
					processing: _toCurrency
				},
				{
					is_coach_only: true,
					title: 'Consistency Rating',
					key: 'stats.consistency',
					processing: _toDefault,
				},
				{
					title: 'Dollars Per Point',
					key: 'stats.cost_divided_by_points',
					processing: _toCurrency
				},
				{
					title: 'Average Points',
					key: 'stats.avg_points',
					processing: _toDefault,
				},
				{
					title: 'Last 3 Average',
					key: 'stats.last_3_avg',
					processing: _toDefault,
				},
				{
					title: '% Owned',
					key: 'stats.owned_by',
					processing: _toPercent
				}
			]
		}
	}

	switchToMulti() {
		this.set({
			max_players_size: 4,
			is_coach_stats: true
		});

		return this;
	}

	switchToh2h() {
		const
			compare = this.get('compare'),
			max_players_size = 2,
			removed_players = compare.splice(max_players_size, max_players_size);

		this.set({ compare, max_players_size, is_coach_stats: false });

		return removed_players;
	}

	generateRoundsRange({ round_id }, silent = true) {
		const TOTAL_ROUNDS_SIZE = process.env.TOTAL_ROUNDS_SIZE;

		const rounds_range = _.range(round_id - 3, round_id + 3)
			.filter(num => num >= 1 && num <= TOTAL_ROUNDS_SIZE)
			.map(num => ({
				id: num,
				is_coach: num >= round_id
			}));

		this.set({ rounds_range }, { silent });

		return this;
	}

	addPlayer({ player }, silent = true) {
		this.set({
			compare: [
				...this.get('compare'),
				{ ...player, stats_table: {} }
			]
		}, { silent });

		return this;
	}

	removePlayer({ player_id }, silent = true) {
		const
			compare = this.get('compare'),
			index = _.findIndex(compare, player => player.id === player_id);

		compare.splice(index, 1);

		this.set({ compare }, { silent });

		return this;
	}

	removePlayers({ players }, silent = true) {
		_.each(players, player => this.removePlayer({ player_id: player.id }, silent));

		return this;
	}

	getMinResult(result){
		if (!_.isNumber(result)) {
			return result;
		}

		const min_result = 228000;
		return result < min_result ? min_result : result
	}

	_getPriceFromPlayerByRound({ player, round_id, is_coach }) {
		const without_result = is_coach && !this.get('has_assistant_coach');

		if (without_result) {
			return {
				original_value: undefined,
				value: '-',
			};
		}

		const
			key = `stats.${ is_coach ? 'proj_' : '' }prices[${round_id}]`,
			result = _.result(player, key),
			min_value_helper = this.getMinResult(result),
			value = _.isNumber(result) ? _.currencyFormat(min_value_helper, '$', true) : '-';
		return {
			value,
			original_value: min_value_helper,
		};
	}

	_getScoreFromPlayerByRound({ player, round_id, is_coach, round }) {
		const without_result = is_coach && !this.get('has_assistant_coach');

		if (without_result) {
			return '-';
		}

		const
			key = `stats.${ is_coach ? 'proj_' : '' }scores[${round_id}]`,
			default_key = is_coach ? '-' : 'DNP',
			is_bye = _.result(round, 'bye_squads', []).includes(player.squad_id);

		return is_bye ? 'BYE' : _.result(player, key, default_key);
	}

	_getOpponentFromMatch({ match, squad_id }) {
		if (match && _.eq(match.away_squad_id, squad_id)) {
			return {
				id: match.home_squad_id,
				side: 'H',
			}
		}

		return {
			id: _.get(match, "away_squad_id", 0),
			side: 'A',
		}
	}

	_getOpponent({ player, round }) {
		const
			{ squad_id } = player,
			{ bye_squads, matches } = round,
			is_bye = bye_squads.includes(squad_id);

		if (is_bye) {
			return { id: 0, side: '-' };
		}

		const
			isPlayerSquad = _.partial(_.eq, squad_id),
			match = _.find(matches, ({ away_squad_id, home_squad_id }) => (
				isPlayerSquad(away_squad_id) || isPlayerSquad(home_squad_id)
			)),
			opponent = this._getOpponentFromMatch({ match, squad_id });

		return {
			id: opponent.id,
			side: opponent.side,
		}
	}

	generateStats() {
		const
			players = this.get('compare'),
			rounds = this.get('rounds'),
			rounds_range = this.get('rounds_range'),
			comparePlayerPriceWithPrev = (player, round_id) => {
				const
					current = _.result(
						player,
						`stats_table[${round_id}].price.original_value`
					),
					prev = _.result(
						player,
						`stats_table[${round_id - 1}].price.original_value`,
						current
					);

				if (!(_.isNumber(current) && _.isNumber(prev))) {
					return 'none';
				}

				if (current > prev) {
					return 'up';
				}

				if (current < prev) {
					return 'down';
				}

				return 'none';
			};

		_.each(players, player => {
			_.each(rounds_range, ({ id, is_coach }) => {
				const round = rounds.find(round => round.id === id);

				_.set(player, `stats_table[${round.id}]`, {
					opponent: this._getOpponent({
						player,
						round
					}),
					price: this._getPriceFromPlayerByRound({
						player,
						is_coach,
						round_id: id,
					}),
					points: this._getScoreFromPlayerByRound({
						player,
						round,
						is_coach,
						round_id: id,
					}),
				});
			});

			_.each(_.keys(player.stats_table).reverse(), round_id => {
				player.stats_table[round_id].price.change = (
					comparePlayerPriceWithPrev(player, round_id)
				);
			});
		});

		return this;
	}

	resetBetterStats() {
		_.each(this.get('compare'), player => player.max_stats = []);

		return this;
	}

	_findBy(players, key, method) {
		return _.reduce(players.slice(), (acc, player) => {
			const result = _.result(player, key);

			return acc.filter(player => _[method](_.result(player, key), result));
		}, players);
	}

	_findByMin(players = [], key = '') {
		return this._findBy(players, key, 'lte');
	}

	_findByMax(players = [], key = '') {
		return this._findBy(players, key, 'gte');
	}

	markBetterStats() {
		const
			players = this.get('compare'),
			stats = this.get('stats'),
			negative_stats = [
				'stats.break_even',
				'stats.cost_divided_by_points'
			];

		_.each(stats, ({ key }) => {
			const
				resultBy = negative_stats.includes(key) ? this._findByMin : this._findByMax,
				players_with_better_stat = resultBy.call(this, players, key);

			if (_.size(players_with_better_stat)) {
				_.each(
					players_with_better_stat,
					player => player.max_stats.push(key)
				);
			}
		});

		return this;
	}

	toggleFavourite(id, is_favourite) {
		_.find(this.get('compare'), { id }).is_favourite = is_favourite;

		return this;
	}

	markAsFavourite(id) {
		return this.toggleFavourite(id, true);
	}

	unMarkAsFavourite(id) {
		return this.toggleFavourite(id, false);
	}
}

export default StatsBox;